import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { localizedPath } from '../utils/routes';
import PhoneVerification from '../components/PhoneVerification';
import SEOHead from '../components/SEOHead';
import SlotPicker from '../components/SlotPicker';
import { isPushSupported, getPushPermission, subscribeToPush, unsubscribeFromPush, syncPushSubscription, registerServiceWorker, getBrowserSubscriptionEndpoint, getServerPushStatus } from '../utils/pushNotifications';

const API = process.env.REACT_APP_API_URL || '/api';

export default function Dashboard() {
  const { user, updateUser } = useAuth();
  const { t, language } = useLanguage();
  const { lang } = useParams();
  const currentLang = lang || language;
  const lp = (routeName) => localizedPath(routeName, currentLang);
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [apptTab, setApptTab] = useState('upcoming');
  const [loading, setLoading] = useState(true);
  const [reviewModal, setReviewModal] = useState({ show: false, appointmentId: null });
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewStatus, setReviewStatus] = useState({ type: '', message: '' });
  const [googlePlaceId, setGooglePlaceId] = useState(null);

  // Reschedule state
  const [rescheduleModal, setRescheduleModal] = useState({ show: false, appointment: null });
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleError, setRescheduleError] = useState('');

  // Top-level section tab
  const [section, setSection] = useState('appointments');

  // Messages state
  const [myMessages, setMyMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [activeThread, setActiveThread] = useState(null);
  const [msgReplyText, setMsgReplyText] = useState('');
  const [msgReplySending, setMsgReplySending] = useState(false);
  const [msgReplyError, setMsgReplyError] = useState('');
  const [msgReplyFiles, setMsgReplyFiles] = useState([]);
  const [msgReplyPreviews, setMsgReplyPreviews] = useState([]);
  const msgReplyFileRef = useRef(null);

  // Profile form state
  const [profileForm, setProfileForm] = useState({ first_name: '', last_name: '', email: '', phone: '', phone2: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ type: '', text: '' });

  // Notification preferences
  const [notifyEmail, setNotifyEmail] = useState(user?.notify_email ?? true);
  const [notifySms, setNotifySms] = useState(user?.notify_sms ?? false);
  const [notifyMarketing, setNotifyMarketing] = useState(user?.notify_marketing ?? false);
  const [prefLang, setPrefLang] = useState(user?.preferred_language || 'fr');
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsMsg, setPrefsMsg] = useState('');
  const [showPhoneVerify, setShowPhoneVerify] = useState(false);
  const [verifyPhoneField, setVerifyPhoneField] = useState('phone'); // 'phone' or 'phone2'
  const [phoneBannerDismissed, setPhoneBannerDismissed] = useState(() => {
    if (!user?.id) return false;
    return localStorage.getItem(`cjpa_phone_dismiss_${user.id}`) === 'true';
  });
  const [pushBannerDismissed, setPushBannerDismissed] = useState(() => {
    if (!user?.id) return false;
    return localStorage.getItem(`cjpa_push_dismiss_${user.id}`) === 'true';
  });

  // Push notification state
  const [pushStatus, setPushStatus] = useState('loading'); // loading|unsupported|default|granted|denied|subscribed

  useEffect(() => {
    loadAppointments();
    loadMessages();
    // Fetch Google Place ID for review-to-Google prompt
    axios.get(`${API}/google-reviews`).then(res => {
      if (res.data?.placeId) setGooglePlaceId(res.data.placeId);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setNotifyEmail(user.notify_email ?? true);
      setNotifySms(user.notify_sms ?? false);
      setNotifyMarketing(user.notify_marketing ?? false);
      setPrefLang(user.preferred_language || 'fr');
      setProfileForm({
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        phone: user.phone || '',
        phone2: user.phone2 || ''
      });
    }
  }, [user]);

  // Check push notification status on mount; verify both browser AND backend
  useEffect(() => {
    async function checkPush() {
      try {
        if (!isPushSupported()) { setPushStatus('unsupported'); return; }
        registerServiceWorker().catch(() => {});
        const perm = getPushPermission();
        if (perm === 'denied') { setPushStatus('denied'); return; }

        const token = localStorage.getItem('cjpa_token');
        const browserEndpoint = await getBrowserSubscriptionEndpoint();
        const serverStatus = token ? await getServerPushStatus(token) : { count: 0 };

        if (perm === 'granted' && browserEndpoint) {
          if (token) { syncPushSubscription(token, true).catch(() => {}); }
          setPushStatus(serverStatus.count > 0 ? 'subscribed' : 'default');
        } else {
          setPushStatus('default');
        }
      } catch {
        setPushStatus('default');
      }
    }
    checkPush();
  }, []);

  const handlePushToggle = async (checked) => {
    const token = localStorage.getItem('cjpa_token');
    if (checked) {
      setPushStatus('loading');
      const result = await subscribeToPush(token, true, language);
      if (result.success) {
        setPushStatus('subscribed');
      } else if (result.reason === 'permission_denied') {
        setPushStatus('denied');
      } else {
        setPushStatus(getPushPermission());
      }
    } else {
      setPushStatus('loading');
      await unsubscribeFromPush(token);
      setPushStatus('default');
    }
  };

  const loadAppointments = async () => {
    try {
      const res = await axios.get(`${API}/appointments`);
      setAppointments(res.data);
    } catch (err) {
      console.error('Error loading appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/contact/mine`);
      setMyMessages(res.data);
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const toggleThread = async (msgId) => {
    if (activeThread?.messageId === msgId) {
      setActiveThread(null);
      setMsgReplyText('');
      setMsgReplyError('');
      clearMsgReplyFiles();
      return;
    }
    try {
      const res = await axios.get(`${API}/contact/${msgId}/thread`);
      setActiveThread({ messageId: msgId, ...res.data });
      setMsgReplyText('');
      setMsgReplyError('');
      clearMsgReplyFiles();
    } catch (err) {
      console.error(err);
    }
  };

  const handleMsgReplyFileChange = (e) => {
    const selected = Array.from(e.target.files).slice(0, 3);
    setMsgReplyFiles(selected);
    setMsgReplyPreviews(selected.map(f => ({ name: f.name, url: URL.createObjectURL(f) })));
  };

  const removeMsgReplyFile = (idx) => {
    const nf = [...msgReplyFiles];
    const np = [...msgReplyPreviews];
    URL.revokeObjectURL(np[idx].url);
    nf.splice(idx, 1);
    np.splice(idx, 1);
    setMsgReplyFiles(nf);
    setMsgReplyPreviews(np);
  };

  const clearMsgReplyFiles = () => {
    msgReplyPreviews.forEach(p => URL.revokeObjectURL(p.url));
    setMsgReplyFiles([]);
    setMsgReplyPreviews([]);
  };

  const submitMessageReply = async (msgId) => {
    if (!msgReplyText.trim()) return;
    setMsgReplySending(true);
    setMsgReplyError('');
    try {
      // Upload attachments first
      let attachmentIds = [];
      if (msgReplyFiles.length > 0) {
        const formData = new FormData();
        msgReplyFiles.forEach(f => formData.append('files', f));
        const uploadRes = await axios.post(`${API}/contact/attachments`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        attachmentIds = uploadRes.data.map(a => a.id);
      }
      await axios.post(`${API}/contact/${msgId}/reply`, {
        body: msgReplyText,
        attachment_ids: attachmentIds
      });
      setMsgReplyText('');
      clearMsgReplyFiles();
      // Refresh thread
      const res = await axios.get(`${API}/contact/${msgId}/thread`);
      setActiveThread({ messageId: msgId, ...res.data });
      loadMessages();
    } catch (err) {
      setMsgReplyError(err.response?.data?.error || (t.dashboard.messages?.replyFailed || 'Failed to send reply'));
    } finally {
      setMsgReplySending(false);
    }
  };

  const openReschedule = (appt) => {
    setRescheduleModal({ show: true, appointment: appt });
    setRescheduleDate('');
    setRescheduleTime('');
    setRescheduleError('');
  };

  const handleReschedule = async () => {
    if (!rescheduleDate || !rescheduleTime) return;
    setRescheduleLoading(true);
    setRescheduleError('');
    try {
      await axios.patch(`${API}/appointments/${rescheduleModal.appointment.id}/reschedule`, {
        appointment_date: rescheduleDate,
        appointment_time: rescheduleTime
      });
      setRescheduleModal({ show: false, appointment: null });
      loadAppointments();
    } catch (err) {
      const data = err.response?.data;
      const msg = language === 'fr' && data?.error_fr ? data.error_fr
        : data?.cutoff_hours ? (t.dashboard.cutoffError || 'Cannot modify within {hours} hours of the appointment').replace('{hours}', data.cutoff_hours)
        : data?.error || 'Error';
      setRescheduleError(msg);
    } finally {
      setRescheduleLoading(false);
    }
  };

  const cancelAppointment = async (id) => {
    if (!window.confirm(t.dashboard.cancelConfirm || t.dashboard.confirmCancel)) return;
    try {
      await axios.patch(`${API}/appointments/${id}/cancel`);
      loadAppointments();
    } catch (err) {
      const data = err.response?.data;
      const msg = language === 'fr' && data?.error_fr ? data.error_fr
        : data?.cutoff_hours ? (t.dashboard.cutoffError || 'Cannot modify within {hours} hours of the appointment').replace('{hours}', data.cutoff_hours)
        : data?.error || t.dashboard.cancelError;
      alert(msg);
    }
  };

  const downloadCalendar = async (id) => {
    try {
      const res = await axios.get(`${API}/appointments/${id}/calendar`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cjpa-appointment-${id}.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Calendar download failed:', err);
    }
  };

  const saveProfile = async () => {
    setProfileSaving(true);
    setProfileMsg({ type: '', text: '' });
    try {
      const res = await axios.put(`${API}/auth/profile`, {
        first_name: profileForm.first_name,
        last_name: profileForm.last_name,
        email: profileForm.email,
        phone: profileForm.phone,
        phone2: profileForm.phone2,
        notify_email: notifyEmail,
        notify_sms: notifySms,
        notify_marketing: notifyMarketing,
        preferred_language: prefLang
      });
      // If backend returned a new token (email changed), update it
      const data = res.data;
      if (data.token) {
        localStorage.setItem('cjpa_token', data.token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
        updateUser(data.user);
      } else {
        updateUser(data);
      }
      setProfileMsg({ type: 'success', text: t.dashboard.profile?.saved || 'Profile updated successfully' });
      setTimeout(() => setProfileMsg({ type: '', text: '' }), 3000);
    } catch (err) {
      if (err.response?.status === 409) {
        setProfileMsg({ type: 'error', text: t.dashboard.profile?.emailInUse || 'This email is already in use' });
      } else {
        setProfileMsg({ type: 'error', text: t.dashboard.profile?.error || 'Failed to update profile' });
      }
    } finally {
      setProfileSaving(false);
    }
  };

  const saveNotificationPrefs = async () => {
    setPrefsSaving(true);
    setPrefsMsg('');
    try {
      const res = await axios.put(`${API}/auth/profile`, {
        first_name: user.first_name,
        last_name: user.last_name,
        phone: user.phone,
        notify_email: notifyEmail,
        notify_sms: notifySms,
        notify_marketing: notifyMarketing,
        preferred_language: prefLang
      });
      updateUser(res.data);
      setPrefsMsg(t.dashboard.notifications?.saved || 'Preferences saved');
      setTimeout(() => setPrefsMsg(''), 3000);
    } catch (err) {
      setPrefsMsg(t.dashboard.notifications?.error || 'Failed to save');
    } finally {
      setPrefsSaving(false);
    }
  };

  const handleSmsToggle = (checked) => {
    if (checked && !user?.phone_verified) {
      setShowPhoneVerify(true);
      return;
    }
    setNotifySms(checked);
  };

  const handlePhoneVerified = async (phone) => {
    setShowPhoneVerify(false);
    if (verifyPhoneField === 'phone2') {
      updateUser({ phone2: phone, phone2_verified: true });
      setProfileForm(prev => ({ ...prev, phone2: phone }));
    } else {
      updateUser({ phone, phone_verified: true });
      setProfileForm(prev => ({ ...prev, phone }));
      setNotifySms(true);
      setPhoneBannerDismissed(false);
      if (user?.id) localStorage.removeItem(`cjpa_phone_dismiss_${user.id}`);
    }

    // Auto-save to backend
    try {
      const payload = {
        first_name: user.first_name,
        last_name: user.last_name,
        phone: verifyPhoneField === 'phone' ? phone : user.phone,
        phone2: verifyPhoneField === 'phone2' ? phone : user.phone2,
        notify_email: notifyEmail,
        notify_sms: verifyPhoneField === 'phone' ? true : notifySms,
        notify_marketing: notifyMarketing,
        preferred_language: prefLang
      };
      const res = await axios.put(`${API}/auth/profile`, payload);
      updateUser(res.data.user || res.data);
      if (verifyPhoneField === 'phone') {
        setPrefsMsg(t.dashboard?.notifications?.smsEnabled || 'SMS notifications enabled!');
      } else {
        setPrefsMsg(t.dashboard?.profile?.phone2Verified || 'Secondary phone verified!');
      }
      setTimeout(() => setPrefsMsg(''), 4000);
    } catch (err) {
      console.error('Failed to save phone preference:', err);
    }
  };

  const dismissPhoneBanner = () => {
    setPhoneBannerDismissed(true);
    if (user?.id) localStorage.setItem(`cjpa_phone_dismiss_${user.id}`, 'true');
  };

  const dismissPushBanner = () => {
    setPushBannerDismissed(true);
    if (user?.id) localStorage.setItem(`cjpa_push_dismiss_${user.id}`, 'true');
  };

  const openReviewModal = (appointmentId) => {
    setReviewModal({ show: true, appointmentId });
    setReviewForm({ rating: 5, comment: '' });
    setReviewStatus({ type: '', message: '' });
  };

  const closeReviewModal = () => {
    setReviewModal({ show: false, appointmentId: null });
    setReviewForm({ rating: 5, comment: '' });
    setReviewStatus({ type: '', message: '' });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/reviews`, {
        appointment_id: reviewModal.appointmentId,
        rating: reviewForm.rating,
        comment: reviewForm.comment || null
      });
      setReviewStatus({ type: 'success', message: t.dashboard.reviewModal.success });
      // If Google Place ID is configured, keep modal open to show Google review prompt
      // Otherwise, auto-close after 2s as before
      if (!googlePlaceId) {
        setTimeout(() => {
          closeReviewModal();
          loadAppointments();
        }, 2000);
      }
    } catch (err) {
      setReviewStatus({
        type: 'error',
        message: err.response?.data?.error || t.dashboard.reviewModal.error
      });
    }
  };

  const upcoming = appointments.filter(a =>
      ['pending', 'confirmed', 'in_progress'].includes(a.status) &&
      new Date(String(a.appointment_date).substring(0, 10) + 'T12:00:00') >= new Date(new Date().toDateString())
  );

  const completed = appointments.filter(a => a.status === 'completed');

  const past = appointments.filter(a =>
      new Date(String(a.appointment_date).substring(0, 10) + 'T12:00:00') < new Date(new Date().toDateString()) &&
      !['completed', 'cancelled'].includes(a.status)
  );

  const cancelled = appointments.filter(a => a.status === 'cancelled');

  const stats = {
    upcoming: upcoming.length,
    completed: appointments.filter(a => a.status === 'completed').length,
    total: appointments.length
  };

  const statusColors = {
    pending: { bg: 'rgba(255,200,100,0.15)', color: '#ffb870' },
    confirmed: { bg: 'rgba(100,200,255,0.15)', color: '#70b8ff' },
    in_progress: { bg: 'rgba(200,100,255,0.15)', color: '#b870ff' },
    completed: { bg: 'rgba(100,200,100,0.15)', color: '#70d070' },
    cancelled: { bg: 'rgba(200,100,100,0.15)', color: '#d07070' }
  };

  const msgStatusColors = {
    new: { bg: 'rgba(100,200,255,0.15)', color: '#70b8ff' },
    read: { bg: 'rgba(255,200,100,0.15)', color: '#ffb870' },
    responded: { bg: 'rgba(100,200,100,0.15)', color: '#70d070' }
  };

  if (loading) return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0C1220' }}>
        <div style={{ color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem', letterSpacing: '0.08em' }}>
          {t.common.loading}
        </div>
      </div>
  );

  const currentList = apptTab === 'upcoming' ? upcoming : apptTab === 'completed' ? completed : apptTab === 'past' ? past : cancelled;

  const sectionTabs = [
    ['appointments', t.dashboard.sections?.appointments || 'Appointments'],
    ['messages', t.dashboard.sections?.messages || 'Messages'],
    ['profile', t.dashboard.sections?.profile || 'Profile'],
    ['settings', t.dashboard.sections?.settings || 'Settings']
  ];

  const inputStyle = {
    width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.25)',
    color: '#E8ECF0', padding: '0.75rem', fontSize: '0.9rem',
    fontFamily: "'Barlow', sans-serif", boxSizing: 'border-box'
  };

  const labelStyle = {
    display: 'block', fontSize: '0.7rem', color: '#94A3B8',
    letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem'
  };

  return (
      <div style={{ background: '#0C1220', minHeight: '100vh', paddingTop: 'var(--cjpa-nav-offset)' }}>
        <SEOHead page="dashboard" />
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: 'clamp(2rem, 4vw, 4rem) clamp(1rem, 3vw, 2rem)' }}>

          {/* Header */}
          <div style={{ marginBottom: '3rem' }}>
            <h1 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '3rem', color: '#E8ECF0', fontWeight: 300, marginBottom: '0.5rem' }}>
              {t.dashboard.welcome}, <span style={{ color: '#D42B2B', fontStyle: 'italic' }}>{user?.first_name}</span>
            </h1>
            <Link to={lp('book')} style={{
              display: 'inline-block', marginTop: '1rem',
              background: '#D42B2B', color: '#0C1220', textDecoration: 'none',
              padding: '0.8rem 2rem', fontFamily: "'Barlow', sans-serif",
              fontSize: '0.7rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600
            }}>
              {t.dashboard.newAppt}
            </Link>
          </div>

          {/* Stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1px', background: 'rgba(148,163,184,0.1)', marginBottom: '3rem' }}>
            {[
              [t.dashboard.upcoming, stats.upcoming],
              [t.dashboard.completed, stats.completed],
              [t.dashboard.total, stats.total]
            ].map(([label, value]) => (
                <div key={label} style={{ background: '#111827', padding: '2rem', textAlign: 'center' }}>
                  <div style={{ fontSize: '2.5rem', color: '#D42B2B', fontFamily: "'Rajdhani', sans-serif", marginBottom: '0.5rem' }}>
                    {value}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.15em', textTransform: 'uppercase' }}>
                    {label}
                  </div>
                </div>
            ))}
          </div>

          {/* Phone Verification Banner */}
          {!user?.phone_verified && !phoneBannerDismissed && (
            <div style={{
              background: 'rgba(212,43,43,0.08)', border: '2px solid rgba(212,43,43,0.4)',
              padding: '1.5rem', marginBottom: '2rem'
            }}>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.3rem', color: '#D42B2B', marginBottom: '0.5rem' }}>
                {t.dashboard.phoneBanner?.title || 'Verify Your Phone Number'}
              </h3>
              <p style={{ color: '#94A3B8', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {t.dashboard.phoneBanner?.message || 'Add and verify your phone number to receive SMS reminders for your appointments.'}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => setShowPhoneVerify(true)} style={{
                  background: '#D42B2B', border: 'none', color: '#0C1220',
                  padding: '0.6rem 1.5rem', fontSize: '0.7rem', letterSpacing: '0.08em',
                  textTransform: 'uppercase', fontWeight: 600, cursor: 'pointer'
                }}>
                  {t.dashboard.phoneBanner?.verify || 'Verify Now'}
                </button>
                <button onClick={dismissPhoneBanner} style={{
                  background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                  color: '#94A3B8', padding: '0.6rem 1.5rem', fontSize: '0.7rem',
                  letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer'
                }}>
                  {t.dashboard.phoneBanner?.dismiss || 'Not Now'}
                </button>
              </div>
            </div>
          )}

          {/* Small reminder when dismissed but still unverified */}
          {!user?.phone_verified && phoneBannerDismissed && (
            <div style={{
              background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.15)',
              padding: '0.75rem 1rem', marginBottom: '2rem', display: 'flex',
              justifyContent: 'space-between', alignItems: 'center'
            }}>
              <span style={{ color: '#94A3B8', fontSize: '0.85rem' }}>
                {t.dashboard.phoneBanner?.reminderSmall || 'Your phone number is not verified.'}
              </span>
              <button onClick={() => setShowPhoneVerify(true)} style={{
                background: 'transparent', border: 'none', color: '#D42B2B',
                fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline'
              }}>
                {t.dashboard.phoneBanner?.verifyLink || 'Verify now'}
              </button>
            </div>
          )}

          {/* Push Notification Banner */}
          {pushStatus === 'default' && !pushBannerDismissed && (
            <div style={{
              background: 'rgba(212,43,43,0.08)', border: '2px solid rgba(212,43,43,0.4)',
              padding: '1.5rem', marginBottom: '2rem'
            }}>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.3rem', color: '#D42B2B', marginBottom: '0.5rem' }}>
                {t.dashboard.pushBanner?.title || 'Enable Push Notifications'}
              </h3>
              <p style={{ color: '#94A3B8', fontSize: '0.9rem', marginBottom: '1rem' }}>
                {t.dashboard.pushBanner?.message || 'Get instant notifications about appointment updates, promotions, and important reminders directly on your device.'}
              </p>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={() => handlePushToggle(true)} style={{
                  background: '#D42B2B', border: 'none', color: '#0C1220',
                  padding: '0.6rem 1.5rem', fontSize: '0.7rem', letterSpacing: '0.08em',
                  textTransform: 'uppercase', fontWeight: 600, cursor: 'pointer'
                }}>
                  {t.dashboard.pushBanner?.enable || 'Enable Now'}
                </button>
                <button onClick={dismissPushBanner} style={{
                  background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                  color: '#94A3B8', padding: '0.6rem 1.5rem', fontSize: '0.7rem',
                  letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer'
                }}>
                  {t.dashboard.pushBanner?.dismiss || 'Not Now'}
                </button>
              </div>
            </div>
          )}

          {/* Section Tabs */}
          <ul className="nav nav-pills mb-4">
            {sectionTabs.map(([key, label]) => (
              <li className="nav-item" key={key}>
                <button
                  className={`nav-link${section === key ? ' active' : ''}`}
                  onClick={() => setSection(key)}
                >
                  {label}
                </button>
              </li>
            ))}
          </ul>

          {/* === APPOINTMENTS TAB === */}
          {section === 'appointments' && (
            <>
              {/* Appointment Sub-tabs */}
              <div style={{ display: 'flex', gap: '1px', background: 'rgba(148,163,184,0.1)', marginBottom: '2rem' }}>
                {[
                  ['upcoming', t.dashboard.tabs.upcoming],
                  ['completed', t.dashboard.tabs.completed || 'Completed'],
                  ['past', t.dashboard.tabs.past],
                  ['cancelled', t.dashboard.tabs.cancelled]
                ].map(([tab, label]) => (
                    <button key={tab} onClick={() => setApptTab(tab)} style={{
                      flex: 1, padding: '1rem',
                      background: apptTab === tab ? '#D42B2B' : 'transparent',
                      color: apptTab === tab ? '#0C1220' : '#94A3B8',
                      border: 'none', fontFamily: "'Barlow', sans-serif",
                      fontSize: '0.7rem', letterSpacing: '0.15em', textTransform: 'uppercase',
                      cursor: 'pointer', fontWeight: 600, transition: 'all 0.3s'
                    }}>
                      {label}
                    </button>
                ))}
              </div>

              {/* Appointments List */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'rgba(148,163,184,0.08)' }}>
                {currentList.length === 0 ? (
                    <div style={{ background: '#111827', padding: '4rem', textAlign: 'center' }}>
                      <p style={{ color: '#94A3B8', fontSize: '1rem', marginBottom: '1.5rem' }}>
                        {t.dashboard.noAppts}
                      </p>
                      <Link to={lp('book')} style={{
                        color: '#D42B2B', textDecoration: 'none', fontSize: '0.9rem',
                        borderBottom: '1px solid #D42B2B', paddingBottom: '2px'
                      }}>
                        {t.dashboard.firstAppt}
                      </Link>
                    </div>
                ) : (
                    currentList.map(appt => (
                        <div key={appt.id} className="cjpa-appt-item" style={{ background: '#111827', padding: 'clamp(1.2rem, 2vw, 2rem)' }}>
                          <div style={{ flex: 1 }}>
                            <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem', color: '#E8ECF0', marginBottom: '0.8rem' }}>
                              {Array.isArray(appt.services) && appt.services.length > 0
                                ? appt.services.map(s => language === 'fr' ? (s.name_fr || s.name) : (s.name_en || s.name)).join(' + ')
                                : (language === 'fr' ? appt.service_name_fr : appt.service_name_en) || appt.service_name}
                            </h3>
                            <div style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: 1.8 }}>
                              <div style={{ marginBottom: '0.3rem' }}>
                                {new Date(String(appt.appointment_date).substring(0, 10) + 'T12:00:00').toLocaleDateString()} {t.dashboard.at} {appt.appointment_time?.substring(0, 5)}
                              </div>
                              <div style={{ marginBottom: '0.3rem' }}>
                                {appt.year} {appt.make} {appt.model}
                              </div>
                              {appt.notes && (
                                  <div style={{ marginTop: '0.8rem', padding: '0.8rem', background: 'rgba(148,163,184,0.05)', border: '1px solid rgba(148,163,184,0.1)', fontSize: '0.85rem', fontStyle: 'italic' }}>
                                    "{appt.notes}"
                                  </div>
                              )}
                              {appt.has_review && (
                                  <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(212,43,43,0.05)', border: '1px solid rgba(212,43,43,0.15)' }}>
                                    <div style={{ fontSize: '0.65rem', color: '#94A3B8', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                                      {t.dashboard.yourReview || 'Your Review'}
                                    </div>
                                    <div style={{ fontSize: '1.2rem', marginBottom: appt.review_comment ? '0.5rem' : 0 }}>
                                      {[1, 2, 3, 4, 5].map(s => (
                                        <span key={s} style={{ color: s <= appt.review_rating ? '#D42B2B' : '#1a2332' }}>★</span>
                                      ))}
                                    </div>
                                    {appt.review_comment && (
                                      <div style={{ color: '#94A3B8', fontSize: '0.85rem', fontStyle: 'italic', marginBottom: '0.4rem' }}>
                                        "{appt.review_comment}"
                                      </div>
                                    )}
                                    <div style={{ color: '#64748B', fontSize: '0.75rem' }}>
                                      {new Date(appt.review_created_at).toLocaleDateString()}
                                    </div>
                                  </div>
                              )}
                            </div>
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.8rem' }}>
                            <span style={{
                              padding: '0.5rem 1rem', fontSize: '0.7rem',
                              background: statusColors[appt.status]?.bg || 'rgba(100,100,100,0.15)',
                              color: statusColors[appt.status]?.color || '#9a9a9a',
                              borderRadius: '3px', letterSpacing: '0.1em', textTransform: 'uppercase'
                            }}>
                              {t.dashboard.status[appt.status] || appt.status}
                            </span>

                            {/* Calendar download for upcoming appointments */}
                            {['pending', 'confirmed'].includes(appt.status) && (
                              <button onClick={() => downloadCalendar(appt.id)} style={{
                                background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                                color: '#94A3B8', padding: '0.4rem 0.8rem', fontSize: '0.65rem',
                                cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase'
                              }}>
                                {t.dashboard.addToCalendar || 'Add to Calendar'}
                              </button>
                            )}

                            {['pending', 'confirmed'].includes(appt.status) && (
                              <>
                                <button onClick={() => openReschedule(appt)} style={{
                                  background: 'transparent', border: '1px solid rgba(148,163,184,0.4)',
                                  color: '#94A3B8', padding: '0.5rem 1rem', fontSize: '0.7rem',
                                  cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase'
                                }}>
                                  {t.dashboard.reschedule || 'Reschedule'}
                                </button>
                                <button onClick={() => cancelAppointment(appt.id)} style={{
                                  background: 'transparent', border: '1px solid rgba(220,70,70,0.4)',
                                  color: '#d07070', padding: '0.5rem 1rem', fontSize: '0.7rem',
                                  cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase'
                                }}>
                                  {t.dashboard.cancel}
                                </button>
                              </>
                            )}

                            {appt.status === 'completed' && !appt.has_review && (
                                <button onClick={() => openReviewModal(appt.id)} style={{
                                  background: '#D42B2B', border: 'none',
                                  color: '#0C1220', padding: '0.5rem 1rem', fontSize: '0.7rem',
                                  cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 600
                                }}>
                                  {t.dashboard.leaveReview}
                                </button>
                            )}

                            {appt.has_review && (
                                <div style={{ color: '#70d070', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
                                  {t.dashboard.reviewed}
                                </div>
                            )}
                          </div>
                        </div>
                    ))
                )}
              </div>
            </>
          )}

          {/* === MESSAGES TAB === */}
          {section === 'messages' && (
            <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '2rem' }}>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#D42B2B', marginBottom: '1.5rem' }}>
                {t.dashboard.messages?.title || 'My Messages'}
              </h2>
              {messagesLoading ? (
                <div style={{ color: '#94A3B8', textAlign: 'center', padding: '2rem' }}>{t.common.loading}</div>
              ) : myMessages.length === 0 ? (
                <div style={{ color: '#94A3B8', textAlign: 'center', padding: '2rem' }}>
                  {t.dashboard.messages?.empty || 'No messages'}
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', background: 'rgba(148,163,184,0.08)' }}>
                  {myMessages.map(msg => (
                    <div key={msg.id} style={{ background: '#0C1220', padding: '1.2rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                        <div style={{ color: '#D42B2B', fontSize: '1rem', fontWeight: 500 }}>{msg.subject}</div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{
                            padding: '0.25rem 0.6rem', fontSize: '0.65rem',
                            background: msgStatusColors[msg.status]?.bg || 'rgba(100,100,100,0.15)',
                            color: msgStatusColors[msg.status]?.color || '#9a9a9a',
                            borderRadius: '3px', letterSpacing: '0.08em', textTransform: 'uppercase'
                          }}>
                            {t.dashboard.messages?.status?.[msg.status] || msg.status}
                          </span>
                        </div>
                      </div>
                      <div style={{ color: '#64748B', fontSize: '0.85rem', marginBottom: '0.5rem' }}>
                        {new Date(msg.created_at).toLocaleDateString()} — {msg.message.length > 100 ? msg.message.substring(0, 100) + '...' : msg.message}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <button
                          onClick={() => toggleThread(msg.id)}
                          style={{
                            background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                            color: '#94A3B8', padding: '0.3rem 0.7rem', fontSize: '0.65rem',
                            cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase'
                          }}
                        >
                          {activeThread?.messageId === msg.id ? (t.dashboard.messages?.hideThread || 'Hide Thread') : (t.dashboard.messages?.viewThread || 'View Thread')}
                        </button>
                        {Number(msg.reply_count) > 0 && (
                          <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>
                            {msg.reply_count} {Number(msg.reply_count) === 1 ? 'reply' : 'replies'}
                          </span>
                        )}
                      </div>

                      {/* Thread view */}
                      {activeThread?.messageId === msg.id && (
                        <div style={{ marginTop: '1rem', borderTop: '1px solid rgba(148,163,184,0.15)', paddingTop: '1rem' }}>
                          {/* Original message */}
                          <div style={{ background: 'rgba(148,163,184,0.05)', padding: '0.75rem', marginBottom: '0.75rem', borderLeft: '3px solid #64748B', fontSize: '0.9rem', color: '#94A3B8' }}>
                            <div style={{ fontWeight: 500, marginBottom: '0.25rem' }}>{msg.name} — {new Date(msg.created_at).toLocaleDateString()}</div>
                            <div style={{ whiteSpace: 'pre-wrap' }}>{msg.message}</div>
                            {activeThread.message?.attachments?.length > 0 && (
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                                {activeThread.message.attachments.map(att => (
                                  <a key={att.id} href={`${API}/contact/attachments/${att.id}`} target="_blank" rel="noopener noreferrer"
                                    style={{ display: 'block', border: '1px solid rgba(148,163,184,0.2)', borderRadius: '4px', overflow: 'hidden' }} title={att.file_name}>
                                    <img src={`${API}/contact/attachments/${att.id}`} alt={att.file_name}
                                      style={{ width: '56px', height: '56px', objectFit: 'cover', display: 'block' }}
                                      onError={e => { e.target.style.display = 'none'; }} />
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Replies */}
                          {activeThread.replies.map(reply => (
                            <div key={reply.id} style={{
                              background: reply.sender_role === 'admin' ? 'rgba(212,43,43,0.05)' : 'rgba(100,150,200,0.05)',
                              padding: '0.75rem', marginBottom: '0.5rem',
                              borderLeft: `3px solid ${reply.sender_role === 'admin' ? '#D42B2B' : '#64748B'}`,
                              fontSize: '0.9rem'
                            }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                <span style={{ color: reply.sender_role === 'admin' ? '#D42B2B' : '#94A3B8', fontWeight: 500 }}>
                                  {reply.sender_name}
                                  {reply.sender_role === 'admin' && <span style={{ fontSize: '0.6rem', background: 'rgba(212,43,43,0.2)', color: '#D42B2B', padding: '0.1rem 0.4rem', marginLeft: '0.5rem', borderRadius: '2px' }}>Admin</span>}
                                </span>
                                <span style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{new Date(reply.created_at).toLocaleDateString()}</span>
                              </div>
                              <div style={{ color: '#E8ECF0', whiteSpace: 'pre-wrap' }}>{reply.body}</div>
                              {reply.attachments?.length > 0 && (
                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                                  {reply.attachments.map(att => (
                                    <a key={att.id} href={`${API}/contact/attachments/${att.id}`} target="_blank" rel="noopener noreferrer"
                                      style={{ display: 'block', border: '1px solid rgba(148,163,184,0.2)', borderRadius: '4px', overflow: 'hidden' }} title={att.file_name}>
                                      <img src={`${API}/contact/attachments/${att.id}`} alt={att.file_name}
                                        style={{ width: '56px', height: '56px', objectFit: 'cover', display: 'block' }}
                                        onError={e => { e.target.style.display = 'none'; }} />
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Reply input */}
                          <div style={{ marginTop: '0.75rem' }}>
                            <textarea
                              value={msgReplyText}
                              onChange={e => setMsgReplyText(e.target.value)}
                              placeholder={t.dashboard.messages?.replyPlaceholder || 'Type your reply...'}
                              rows={3}
                              style={{
                                width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.25)',
                                color: '#E8ECF0', padding: '0.75rem', fontSize: '0.85rem',
                                fontFamily: "'Barlow', sans-serif", resize: 'vertical', boxSizing: 'border-box'
                              }}
                            />

                            {/* Attachment controls */}
                            <div style={{ marginTop: '0.5rem' }}>
                              <input
                                ref={msgReplyFileRef}
                                type="file"
                                accept="image/jpeg,image/png,image/gif,image/webp"
                                multiple
                                onChange={handleMsgReplyFileChange}
                                style={{ display: 'none' }}
                              />
                              <button
                                type="button"
                                onClick={() => msgReplyFileRef.current?.click()}
                                disabled={msgReplySending || msgReplyFiles.length >= 3}
                                style={{
                                  background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                                  color: '#94A3B8', padding: '0.35rem 0.75rem', fontSize: '0.7rem',
                                  cursor: msgReplySending || msgReplyFiles.length >= 3 ? 'not-allowed' : 'pointer',
                                  opacity: msgReplyFiles.length >= 3 ? 0.5 : 1
                                }}
                              >
                                {t.dashboard.messages?.attachImages || 'Attach Images'}
                              </button>
                              <span style={{ fontSize: '0.65rem', color: '#64748B', marginLeft: '0.5rem' }}>
                                {t.dashboard.messages?.attachHint || 'Max 3 images, 5MB each'}
                              </span>
                              {msgReplyPreviews.length > 0 && (
                                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                                  {msgReplyPreviews.map((p, idx) => (
                                    <div key={idx} style={{ position: 'relative' }}>
                                      <img src={p.url} alt={p.name}
                                        style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '4px', border: '1px solid rgba(148,163,184,0.2)' }} />
                                      <button type="button" onClick={() => removeMsgReplyFile(idx)}
                                        aria-label={t.common.close}
                                        style={{
                                          position: 'absolute', top: '-6px', right: '-6px',
                                          background: '#D42B2B', border: 'none', color: '#fff',
                                          width: '18px', height: '18px', borderRadius: '50%',
                                          fontSize: '0.65rem', lineHeight: '18px', textAlign: 'center',
                                          cursor: 'pointer', padding: 0
                                        }}>&times;</button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>

                            {msgReplyError && (
                              <div style={{ color: '#d07070', fontSize: '0.8rem', marginTop: '0.3rem' }}>{msgReplyError}</div>
                            )}
                            <button
                              onClick={() => submitMessageReply(msg.id)}
                              disabled={msgReplySending || !msgReplyText.trim()}
                              style={{
                                marginTop: '0.5rem', background: '#D42B2B', border: 'none',
                                color: '#0C1220', padding: '0.5rem 1.2rem', fontSize: '0.7rem',
                                letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
                                cursor: msgReplySending || !msgReplyText.trim() ? 'not-allowed' : 'pointer',
                                opacity: msgReplySending || !msgReplyText.trim() ? 0.5 : 1
                              }}
                            >
                              {msgReplySending ? (t.dashboard.messages?.sending || 'Sending...') : (t.dashboard.messages?.sendReply || 'Send Reply')}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* === PROFILE TAB === */}
          {section === 'profile' && (
            <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '2rem' }}>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#D42B2B', marginBottom: '1.5rem' }}>
                {t.dashboard.profile?.title || 'Profile Information'}
              </h2>

              {profileMsg.text && (
                <div style={{
                  padding: '0.6rem 1rem', marginBottom: '1rem',
                  background: profileMsg.type === 'success' ? 'rgba(100,200,100,0.15)' : 'rgba(220,70,70,0.15)',
                  border: profileMsg.type === 'success' ? '1px solid rgba(100,200,100,0.4)' : '1px solid rgba(220,70,70,0.4)',
                  color: profileMsg.type === 'success' ? '#70d070' : '#d07070',
                  fontSize: '0.85rem'
                }}>
                  {profileMsg.text}
                </div>
              )}

              <div className="cjpa-form-2col" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
                <div>
                  <label style={labelStyle}>{t.dashboard.profile?.firstName || 'First Name'}</label>
                  <input
                    type="text"
                    value={profileForm.first_name}
                    onChange={e => setProfileForm(prev => ({ ...prev, first_name: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t.dashboard.profile?.lastName || 'Last Name'}</label>
                  <input
                    type="text"
                    value={profileForm.last_name}
                    onChange={e => setProfileForm(prev => ({ ...prev, last_name: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t.dashboard.profile?.email || 'Email Address'}</label>
                  <input
                    type="email"
                    value={profileForm.email}
                    onChange={e => setProfileForm(prev => ({ ...prev, email: e.target.value }))}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>{t.dashboard.profile?.phone || 'Phone Number'}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <input
                      type="tel"
                      value={profileForm.phone}
                      onChange={e => setProfileForm(prev => ({ ...prev, phone: e.target.value }))}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {user?.phone_verified ? (
                      <span style={{ fontSize: '0.75rem', color: '#70d070', whiteSpace: 'nowrap' }}>
                        {t.dashboard.profile?.phoneVerified || 'Phone verified'}
                      </span>
                    ) : (
                      <button
                        onClick={() => { setVerifyPhoneField('phone'); setShowPhoneVerify(true); }}
                        style={{
                          background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                          color: '#94A3B8', padding: '0.4rem 0.8rem', fontSize: '0.65rem',
                          cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap'
                        }}
                      >
                        {t.dashboard.profile?.verifyPhone || 'Verify Phone'}
                      </button>
                    )}
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>{t.dashboard.profile?.phone2 || 'Secondary Phone'}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <input
                      type="tel"
                      value={profileForm.phone2}
                      onChange={e => setProfileForm(prev => ({ ...prev, phone2: e.target.value }))}
                      placeholder={t.dashboard.profile?.phone2Hint || 'Optional'}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    {user?.phone2_verified ? (
                      <span style={{ fontSize: '0.75rem', color: '#70d070', whiteSpace: 'nowrap' }}>
                        {t.dashboard.profile?.phone2Verified || 'Verified'}
                      </span>
                    ) : profileForm.phone2 && profileForm.phone2.length >= 7 ? (
                      <button
                        onClick={() => { setVerifyPhoneField('phone2'); setShowPhoneVerify(true); }}
                        style={{
                          background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                          color: '#94A3B8', padding: '0.4rem 0.8rem', fontSize: '0.65rem',
                          cursor: 'pointer', letterSpacing: '0.08em', textTransform: 'uppercase', whiteSpace: 'nowrap'
                        }}
                      >
                        {t.dashboard.profile?.verifyPhone || 'Verify'}
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>

              <button
                onClick={saveProfile}
                disabled={profileSaving}
                style={{
                  background: '#D42B2B', border: 'none',
                  color: '#0C1220', padding: '0.8rem 2rem', fontSize: '0.7rem',
                  letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600,
                  cursor: profileSaving ? 'not-allowed' : 'pointer',
                  opacity: profileSaving ? 0.5 : 1
                }}
              >
                {profileSaving ? (t.dashboard.profile?.saving || 'Saving...') : (t.dashboard.profile?.save || 'Save Profile')}
              </button>
            </div>
          )}

          {/* === SETTINGS TAB === */}
          {section === 'settings' && (
            <div style={{ background: '#111827', border: '1px solid rgba(148,163,184,0.15)', padding: '2rem' }}>
              <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.8rem', color: '#D42B2B', marginBottom: '1.5rem' }}>
                {t.dashboard.notifications?.title || 'Notification Preferences'}
              </h2>

              {prefsMsg && (
                <div style={{ padding: '0.6rem 1rem', marginBottom: '1rem', background: 'rgba(100,200,100,0.15)', border: '1px solid rgba(100,200,100,0.4)', color: '#70d070', fontSize: '0.85rem' }}>
                  {prefsMsg}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '1.5rem' }}>
                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="notifyEmail"
                    checked={notifyEmail} onChange={e => setNotifyEmail(e.target.checked)} />
                  <label className="form-check-label" htmlFor="notifyEmail" style={{ color: '#E8ECF0' }}>
                    {t.dashboard.notifications?.emailToggle || 'Email notifications'}
                  </label>
                </div>

                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="notifySms"
                    checked={notifySms} onChange={e => handleSmsToggle(e.target.checked)} />
                  <label className="form-check-label" htmlFor="notifySms" style={{ color: '#E8ECF0' }}>
                    {t.dashboard.notifications?.smsToggle || 'SMS notifications'}
                  </label>
                  {!user?.phone_verified && (
                    <div style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '0.3rem' }}>
                      {t.dashboard.notifications?.smsVerifyHint || 'Verify your phone number to enable SMS'}
                    </div>
                  )}
                  {user?.phone_verified && (
                    <div style={{ fontSize: '0.75rem', color: '#70d070', marginTop: '0.3rem' }}>
                      {t.dashboard.profile?.phoneVerified || 'Phone verified'}
                    </div>
                  )}
                </div>

                {pushStatus !== 'unsupported' && (
                  <div className="form-check form-switch">
                    <input className="form-check-input" type="checkbox" id="notifyPush"
                      checked={pushStatus === 'subscribed'}
                      disabled={pushStatus === 'denied' || pushStatus === 'loading'}
                      onChange={e => handlePushToggle(e.target.checked)} />
                    <label className="form-check-label" htmlFor="notifyPush" style={{ color: '#E8ECF0' }}>
                      {t.dashboard.notifications?.pushToggle || 'Push notifications'}
                    </label>
                    {pushStatus === 'denied' && (
                      <div style={{ fontSize: '0.75rem', color: '#D42B2B', marginTop: '0.3rem' }}>
                        {t.dashboard.notifications?.pushDenied || 'Push blocked — enable in browser settings'}
                      </div>
                    )}
                  </div>
                )}

                <div className="form-check form-switch">
                  <input className="form-check-input" type="checkbox" id="notifyMarketing"
                    checked={notifyMarketing} onChange={e => setNotifyMarketing(e.target.checked)} />
                  <label className="form-check-label" htmlFor="notifyMarketing" style={{ color: '#E8ECF0' }}>
                    {t.dashboard.notifications?.marketingToggle || 'Marketing emails'}
                  </label>
                  <div style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: '0.3rem' }}>
                    {t.dashboard.notifications?.marketingHint || 'Receive promotions and news from us'}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', color: '#94A3B8', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
                    {t.dashboard.notifications?.preferredLang || 'Preferred Language'}
                  </label>
                  <select className="form-select form-select-sm" value={prefLang} onChange={e => setPrefLang(e.target.value)} style={{ width: 'auto' }}>
                    <option value="fr">Fran&ccedil;ais (Canada)</option>
                    <option value="en">English (Canada)</option>
                  </select>
                </div>
              </div>

              <button className="btn btn-primary btn-sm" onClick={saveNotificationPrefs} disabled={prefsSaving}>
                {prefsSaving ? (t.dashboard.notifications?.saving || 'Saving...') : (t.dashboard.notifications?.save || 'Save')}
              </button>
            </div>
          )}

        </div>

        {/* Phone Verification Modal */}
        {showPhoneVerify && (
          <PhoneVerification
            onVerified={handlePhoneVerified}
            onClose={() => setShowPhoneVerify(false)}
            phoneField={verifyPhoneField}
          />
        )}

        {/* Review Modal */}
        {reviewModal.show && (
            <div style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', zIndex: 2000, padding: '2rem'
            }} onClick={closeReviewModal}>
              <div onClick={e => e.stopPropagation()} style={{
                background: '#111827', border: '1px solid rgba(148,163,184,0.3)',
                padding: '3rem', maxWidth: '500px', width: '100%'
              }}>
                <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#E8ECF0', marginBottom: '2rem' }}>
                  {t.dashboard.reviewModal.title}
                </h2>

                {reviewStatus.type === 'success' && googlePlaceId ? (
                    <div>
                      <div style={{
                        padding: '1rem', marginBottom: '1.5rem',
                        background: 'rgba(100,200,100,0.15)',
                        border: '1px solid rgba(100,200,100,0.4)',
                        color: '#70d070'
                      }}>
                        {reviewStatus.message}
                      </div>
                      <a
                        href={`https://search.google.com/local/writereview?placeid=${googlePlaceId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'block', width: '100%', textAlign: 'center',
                          background: '#D42B2B', border: 'none', color: '#0C1220',
                          padding: '1rem', fontSize: '0.7rem', letterSpacing: '0.15em',
                          textTransform: 'uppercase', fontWeight: 600, textDecoration: 'none',
                          marginBottom: '0.75rem', boxSizing: 'border-box'
                        }}
                      >
                        {t.dashboard.reviewModal.googleReview}
                      </a>
                      <button onClick={() => { closeReviewModal(); loadAppointments(); }} style={{
                        width: '100%', background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                        color: '#94A3B8', padding: '1rem', fontSize: '0.7rem',
                        letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer'
                      }}>
                        {t.dashboard.reviewModal.done}
                      </button>
                    </div>
                ) : (
                  <>
                    {reviewStatus.message && (
                        <div style={{
                          padding: '1rem', marginBottom: '1.5rem',
                          background: reviewStatus.type === 'success' ? 'rgba(100,200,100,0.15)' : 'rgba(220,70,70,0.15)',
                          border: reviewStatus.type === 'success' ? '1px solid rgba(100,200,100,0.4)' : '1px solid rgba(220,70,70,0.4)',
                          color: reviewStatus.type === 'success' ? '#70d070' : '#d07070'
                        }}>
                          {reviewStatus.message}
                        </div>
                    )}

                    <form onSubmit={submitReview}>
                      <div style={{ marginBottom: '2rem' }}>
                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#7C8CA0', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '1rem' }}>
                          {t.dashboard.reviewModal.rating}
                        </label>
                        <div style={{ display: 'flex', gap: '0.5rem', fontSize: '2rem' }}>
                          {[1, 2, 3, 4, 5].map(star => (
                              <button
                                  key={star}
                                  type="button"
                                  onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                                  style={{
                                    background: 'transparent', border: 'none',
                                    fontSize: '2.5rem', cursor: 'pointer',
                                    color: star <= reviewForm.rating ? '#D42B2B' : '#1a2332',
                                    transition: 'color 0.2s'
                                  }}
                              >
                                ★
                              </button>
                          ))}
                        </div>
                      </div>

                      <div style={{ marginBottom: '2rem' }}>
                        <label style={{ display: 'block', fontSize: '0.7rem', color: '#7C8CA0', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '0.8rem' }}>
                          {t.dashboard.reviewModal.comment}
                        </label>
                        <textarea
                            value={reviewForm.comment}
                            onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                            placeholder={t.dashboard.reviewModal.placeholder}
                            rows={4}
                            style={{
                              width: '100%', background: '#1a2332', border: '1px solid rgba(148,163,184,0.25)',
                              color: '#E8ECF0', padding: '1rem', fontSize: '0.9rem',
                              fontFamily: "'Barlow', sans-serif", resize: 'vertical', boxSizing: 'border-box'
                            }}
                        />
                      </div>

                      <div style={{ display: 'flex', gap: '1rem' }}>
                        <button type="button" onClick={closeReviewModal} style={{
                          flex: 1, background: 'transparent', border: '1px solid rgba(148,163,184,0.3)',
                          color: '#D42B2B', padding: '1rem', fontSize: '0.7rem',
                          letterSpacing: '0.15em', textTransform: 'uppercase', cursor: 'pointer'
                        }}>
                          {t.dashboard.reviewModal.cancel}
                        </button>
                        <button type="submit" disabled={reviewStatus.type === 'success'} style={{
                          flex: 1, background: '#D42B2B', border: 'none',
                          color: '#0C1220', padding: '1rem', fontSize: '0.7rem',
                          letterSpacing: '0.15em', textTransform: 'uppercase', fontWeight: 600,
                          cursor: reviewStatus.type === 'success' ? 'not-allowed' : 'pointer',
                          opacity: reviewStatus.type === 'success' ? 0.5 : 1
                        }}>
                          {reviewStatus.type === 'success' ? t.dashboard.reviewModal.success : t.dashboard.reviewModal.submit}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
        )}

        {/* Reschedule Modal */}
        {rescheduleModal.show && (
          <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '1rem'
          }}>
            <div style={{
              background: '#111827', border: '1px solid rgba(148,163,184,0.2)',
              padding: '2rem', maxWidth: '500px', width: '100%', maxHeight: '90vh', overflowY: 'auto'
            }}>
              <h3 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.5rem', color: '#E8ECF0', marginBottom: '1.5rem' }}>
                {t.dashboard.rescheduleTitle || 'Reschedule Appointment'}
              </h3>
              <p style={{ color: '#94A3B8', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
                {Array.isArray(rescheduleModal.appointment?.services) && rescheduleModal.appointment.services.length > 0
                  ? rescheduleModal.appointment.services.map(s => language === 'fr' ? (s.name_fr || s.name) : (s.name_en || s.name)).join(' + ')
                  : (language === 'fr' ? rescheduleModal.appointment?.service_name_fr : rescheduleModal.appointment?.service_name_en) || rescheduleModal.appointment?.service_name}
              </p>

              {rescheduleError && <div className="alert alert-danger" style={{ fontSize: '0.85rem' }}>{rescheduleError}</div>}

              <SlotPicker
                serviceIds={Array.isArray(rescheduleModal.appointment?.services) && rescheduleModal.appointment.services.length > 0
                  ? rescheduleModal.appointment.services.map(s => s.id)
                  : rescheduleModal.appointment?.service_id ? [rescheduleModal.appointment.service_id] : []}
                selectedDate={rescheduleDate}
                selectedTime={rescheduleTime}
                onDateChange={val => { setRescheduleDate(val); setRescheduleTime(''); setRescheduleError(''); }}
                onTimeChange={setRescheduleTime}
                language={language}
                t={t}
              />

              <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
                <button onClick={() => setRescheduleModal({ show: false, appointment: null })}
                  className="btn btn-outline-primary" style={{ padding: '0.8rem 1.5rem' }}>
                  {t.common.cancel}
                </button>
                <button onClick={handleReschedule} disabled={!rescheduleDate || !rescheduleTime || rescheduleLoading}
                  className="btn btn-primary" style={{ padding: '0.8rem 1.5rem' }}>
                  {rescheduleLoading ? t.common.loading : (t.dashboard.rescheduleConfirm || 'Confirm New Time')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}
